import { z } from 'zod'

declare const instantBrand: unique symbol
declare const localDateBrand: unique symbol
declare const durationSecondsBrand: unique symbol
declare const ianaTimeZoneBrand: unique symbol

export type Instant = string & { readonly [instantBrand]: true }
export type LocalDate = string & { readonly [localDateBrand]: true }
export type DurationSeconds = number & { readonly [durationSecondsBrand]: true }
export type IanaTimeZone = string & { readonly [ianaTimeZoneBrand]: true }

const INSTANT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,9})?Z$/
const LOCAL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

function isValidUtcInstant(value: string): boolean {
  const match = INSTANT_PATTERN.exec(value)
  if (!match) return false

  const [, year, month, day, hour, minute, second] = match
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return false

  return (
    parsed.getUTCFullYear() === Number(year) &&
    parsed.getUTCMonth() + 1 === Number(month) &&
    parsed.getUTCDate() === Number(day) &&
    parsed.getUTCHours() === Number(hour) &&
    parsed.getUTCMinutes() === Number(minute) &&
    parsed.getUTCSeconds() === Number(second)
  )
}

function isValidLocalDate(value: string): boolean {
  const match = LOCAL_DATE_PATTERN.exec(value)
  if (!match) return false

  const [, year, month, day] = match
  const parsed = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))

  return (
    parsed.getUTCFullYear() === Number(year) &&
    parsed.getUTCMonth() + 1 === Number(month) &&
    parsed.getUTCDate() === Number(day)
  )
}

function isIanaTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format()
    return value.includes('/') || value === 'UTC'
  } catch {
    return false
  }
}

export const InstantSchema = z
  .string()
  .refine(isValidUtcInstant, 'Expected an RFC 3339 UTC instant ending in Z')
  .transform((value) => value as Instant)

export const LocalDateSchema = z
  .string()
  .refine(isValidLocalDate, 'Expected a calendar date in YYYY-MM-DD format')
  .transform((value) => value as LocalDate)

export const DurationSecondsSchema = z
  .number()
  .safe()
  .int()
  .nonnegative()
  .transform((value) => value as DurationSeconds)

export const IanaTimeZoneSchema = z
  .string()
  .refine(isIanaTimeZone, 'Expected an IANA time zone')
  .transform((value) => value as IanaTimeZone)

export function instantFromDate(value: Date): Instant {
  if (Number.isNaN(value.getTime())) throw new RangeError('Cannot serialize an invalid Date')
  return InstantSchema.parse(value.toISOString())
}

/**
 * Fecha de calendario a partir de un `Date` de Mongo.
 *
 * Toma la parte **UTC**, y no la local, porque así fue guardada: un motor sin
 * tipo fecha representa un día como su medianoche UTC. Leerla en la zona local
 * la corre un día entero para medio planeta — una promesa que vence el 30 se
 * leería como vencida el 29 en América.
 */
export function localDateFromDate(value: Date): LocalDate {
  if (Number.isNaN(value.getTime())) throw new RangeError('Cannot serialize an invalid Date')
  return LocalDateSchema.parse(value.toISOString().slice(0, 10))
}
